import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Star,
  MapPin,
  Clock,
  Shield,
  Car,
  Calendar,
  ChevronLeft,
  CreditCard,
  Award,
  BookOpen,
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  Heart,
  Send,
  Share2,
  Check,
  Instagram,
  Linkedin,
  Phone,
  ChevronRight,
  ZoomIn,
  X,
  Bell,
  Download,
  Languages,
  ExternalLink,
  GraduationCap,
  CalendarDays,
  Palmtree,
  Package,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { AnimatePresence, motion } from 'framer-motion'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/useAuth'
import { useSEO } from '@/hooks/useSEO'
import { useInstructor, instructorKeys } from '@/hooks/useInstructors'
import { useFavoriteIds, useToggleFavorite } from '@/hooks/useFavorites'
import { useInstructorEnhancedFields } from '@/hooks/useInstructorProfile'
import { useInstructorVehicles } from '@/hooks/useInstructorVehicles'
import { supabase } from '@/lib/supabase'
import { SPECIALTY_LABELS, DAY_OF_WEEK_LABELS } from '@/lib/constants'
import type { InstructorWithProfile } from '@/types/database'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { InquiryChat } from '@/components/shared/InquiryChat'
import { InstructorServiceAreaMap } from '@/components/instructor/InstructorServiceAreaMap'
import { InstructorAnnouncementCard } from '@/components/instructor/InstructorAnnouncementCard'
import { InstructorFeedCard } from '@/components/instructor/InstructorFeedCard'
import { useInstructorGallery } from '@/hooks/useInstructorGallery'
import { useInstructorTags, useAllTagDefinitions } from '@/hooks/useInstructorTags'
import { PremiumBadge } from '@/components/student/PremiumBadge'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { formatCurrency, getStudentPrice } from '@/lib/utils'
import { useInstructorPackages } from '@/hooks/useLessonPackages'
import { usePurchasePackage, useUsablePackagesForInstructor } from '@/hooks/usePackages'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Review {
  id: string
  student_name: string
  student_avatar?: string
  student_is_premium?: boolean
  rating: number
  comment: string
  instructor_reply: string | null
  instructor_reply_at: string | null
  created_at: string
}

interface TimeSlot {
  start: string
  end: string
}

interface DayAvailability {
  day: string
  slots: TimeSlot[]
}

const LANGUAGE_LABELS: Record<string, string> = {
  pt: 'Português',
  en: 'Inglês',
  es: 'Espanhol',
  fr: 'Francês',
  de: 'Alemão',
  it: 'Italiano',
  ja: 'Japonês',
  zh: 'Mandarim',
  ar: 'Árabe',
}

const DURATION_OPTIONS = [
  { minutes: 60, label: '1h' },
  { minutes: 90, label: '1h 30min' },
  { minutes: 120, label: '2h' },
] as const

const REVIEW_TAGS = [
  'Muito paciente',
  'Pontual',
  'Boa didática',
  'Carro bem cuidado',
  'Recomendo',
  'Ótima comunicação',
  'Explicações claras',
  'Ambiente tranquilo',
]

const REVIEWS_PER_PAGE = 5

// ─── Sub-components ───────────────────────────────────────────────────────────

// ─── S2: Galeria de fotos (tab público) ───────────────────────────────────────

function InstructorGalleryTab({ instructorId }: { instructorId: string }) {
  const { data: photos = [], isLoading } = useInstructorGallery(instructorId)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="aspect-square rounded-xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    )
  }

  if (photos.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-gray-200 py-12 text-center text-muted-foreground">
        <p className="text-sm">Nenhuma foto na galeria ainda.</p>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setPreviewUrl(photo.public_url ?? null)}
            className="group relative aspect-square overflow-hidden rounded-xl border border-gray-200 bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {photo.public_url && (
              <img
                src={photo.public_url}
                alt={photo.caption ?? 'Foto do instrutor'}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            )}
            {photo.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-[11px] text-white truncate">{photo.caption}</p>
              </div>
            )}
          </button>
        ))}
      </div>

      <Dialog open={Boolean(previewUrl)} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Galeria</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Visualização"
              className="w-full rounded-xl object-contain max-h-[70vh]"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

// ─── R2: Veículos disponíveis do instrutor ────────────────────────────────────

function InstructorVehiclesSection({ instructorId }: { instructorId: string }) {
  const { data: vehicles = [] } = useInstructorVehicles(instructorId)
  const activeVehicles = vehicles.filter((v) => v.is_active)

  if (activeVehicles.length === 0) return null

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Car className="h-5 w-5 text-gray-500" />
        Veículos disponíveis
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {activeVehicles.map((v) => (
          <div
            key={v.id}
            className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm border border-gray-100">
              <Car className="h-4 w-4 text-brand-500" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-gray-900 truncate">
                {v.make} {v.model}
                {v.year && <span className="font-normal text-muted-foreground ml-1">({v.year})</span>}
              </p>
              {v.type && (
                <p className="text-xs text-muted-foreground">{v.type}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Vehicle gallery + lightbox ──────────────────────────────────────────────

function VehicleGallery({ photos }: { photos: string[] }) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null)

  if (!photos.length) return null

  function prev() {
    setLightboxIdx((i) => (i === null ? null : (i - 1 + photos.length) % photos.length))
  }
  function next() {
    setLightboxIdx((i) => (i === null ? null : (i + 1) % photos.length))
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Car className="h-5 w-5 text-gray-500" />
        Fotos do veículo
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((url, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setLightboxIdx(i)}
            className="group relative aspect-video rounded-xl overflow-hidden bg-gray-100"
          >
            <img
              src={url}
              alt={`Veículo ${i + 1}`}
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
              // Foto que não carrega some (em vez do ícone de imagem quebrada).
              onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none' }}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightboxIdx(null)}
          >
            <motion.div
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.92 }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={photos[lightboxIdx]}
                alt={`Veículo ${lightboxIdx + 1}`}
                className="w-full max-h-[80vh] object-contain rounded-xl"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                <span className="text-white/70 text-sm bg-black/40 rounded-full px-3 py-1">
                  {lightboxIdx + 1} / {photos.length}
                </span>
                <button
                  onClick={() => setLightboxIdx(null)}
                  className="text-white bg-black/40 rounded-full p-1.5 hover:bg-black/60"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {photos.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'h-6 w-6' : size === 'md' ? 'h-5 w-5' : 'h-4 w-4'
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= Math.round(rating)
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-gray-200 text-gray-200'
          }`}
        />
      ))}
    </div>
  )
}

function RatingBreakdown({ reviews }: { reviews: Review[] }) {
  const total = reviews.length
  const counts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => Math.round(r.rating) === star).length,
    pct: total > 0 ? (reviews.filter((r) => Math.round(r.rating) === star).length / total) * 100 : 0,
  }))

  const avg =
    total > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / total
      : 0

  return (
    <div className="flex gap-8 items-center">
      <div className="text-center flex-shrink-0">
        <p className="text-5xl font-bold text-gray-900">{avg.toFixed(1)}</p>
        <StarRating rating={avg} size="md" />
        <p className="text-sm text-gray-400 mt-1">{total} avaliações</p>
      </div>
      <div className="flex-1 space-y-1.5">
        {counts.map(({ star, count, pct }) => (
          <div key={star} className="flex items-center gap-2 text-sm">
            <span className="w-3 text-gray-500">{star}</span>
            <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400 flex-shrink-0" />
            <div className="flex-1 bg-gray-100 rounded-full h-2">
              <div
                className="bg-yellow-400 h-2 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-8 text-right text-gray-400">{count}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ReviewCard({ review, isOwnProfile }: { review: Review; isOwnProfile?: boolean }) {
  const initials = review.student_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const [reply, setReply] = useState(review.instructor_reply ?? '')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(reply)
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    const { error } = await supabase
      .from('reviews')
      .update({ instructor_reply: draft.trim(), instructor_reply_at: new Date().toISOString() })
      .eq('id', review.id)
    setSaving(false)
    if (error) {
      toast.error('Erro ao salvar resposta.')
    } else {
      setReply(draft.trim())
      setEditing(false)
      toast.success('Resposta salva!')
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={review.student_avatar} alt={review.student_name} />
            <AvatarFallback className="bg-brand-100 text-brand-700 text-sm font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-gray-900 text-sm flex items-center gap-1.5 flex-wrap">
              {review.student_name}
              {review.student_is_premium && <PremiumBadge />}
            </p>
            <StarRating rating={review.rating} />
          </div>
        </div>
        <span className="text-xs text-gray-400 flex-shrink-0">
          {format(new Date(review.created_at), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
        </span>
      </div>
      {review.comment && (
        <p className="text-sm text-gray-600 leading-relaxed">{review.comment}</p>
      )}

      {/* Instructor reply */}
      {reply && !editing && (
        <div className="ml-4 border-l-2 border-brand-200 pl-4 space-y-1">
          <p className="text-xs font-semibold text-brand-700 flex items-center gap-1">
            <MessageCircle className="h-3 w-3" />
            Resposta do instrutor
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">{reply}</p>
          {isOwnProfile && (
            <button
              onClick={() => { setDraft(reply); setEditing(true) }}
              className="text-xs text-muted-foreground hover:text-brand-600 transition-colors"
            >
              Editar resposta
            </button>
          )}
        </div>
      )}

      {/* Reply editor (instructor only) */}
      {isOwnProfile && !reply && !editing && (
        <button
          onClick={() => { setDraft(''); setEditing(true) }}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-brand-600 transition-colors"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          Responder avaliação
        </button>
      )}
      {editing && (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Escreva sua resposta pública..."
            rows={2}
            maxLength={400}
            className="text-sm"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving || !draft.trim()} className="h-7 text-xs">
              Salvar resposta
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs">
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function AvailabilityTable({ availability }: { availability: DayAvailability[] }) {
  if (availability.length === 0) {
    return (
      <div className="text-center py-10 text-gray-400">
        <Calendar className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p>Nenhuma disponibilidade cadastrada ainda.</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-3 px-4 font-semibold text-gray-600">Dia</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-600">Horários disponíveis</th>
          </tr>
        </thead>
        <tbody>
          {availability.map((day) => (
            <tr key={day.day} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 font-medium text-gray-900 capitalize">{day.day}</td>
              <td className="py-3 px-4">
                {day.slots.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {day.slots.map((slot) => (
                      <Badge
                        key={`${slot.start}-${slot.end}`}
                        variant="secondary"
                        className="text-xs"
                      >
                        {slot.start} – {slot.end}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-400">Indisponível</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Interactive star rating ──────────────────────────────────────────────────

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0)
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="focus:outline-none"
        >
          <Star
            className={`h-8 w-8 transition-colors ${
              star <= (hovered || value)
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-100 text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  )
}

// ─── Review form ──────────────────────────────────────────────────────────────

interface WriteReviewFormProps {
  instructorId: string
  instructorSlug: string
  userId: string
}

function WriteReviewForm({ instructorId, instructorSlug, userId }: WriteReviewFormProps) {
  const queryClient = useQueryClient()
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [showName, setShowName] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const { data: eligibleBookingId } = useQuery({
    queryKey: ['review-eligibility', userId, instructorId],
    queryFn: async () => {
      const { data } = await supabase
        .from('bookings')
        .select('id')
        .eq('student_id', userId)
        .eq('instructor_id', instructorId)
        .eq('status', 'completed')
        .limit(1)
        .single()
      return data?.id ?? null
    },
    enabled: Boolean(userId) && Boolean(instructorId),
    staleTime: 1000 * 60 * 5,
  })

  const { data: alreadyReviewed } = useQuery({
    queryKey: ['already-reviewed', userId, instructorId],
    queryFn: async () => {
      const { data } = await supabase
        .from('reviews')
        .select('id')
        .eq('student_id', userId)
        .eq('instructor_id', instructorId)
        .limit(1)
        .maybeSingle()
      return Boolean(data)
    },
    enabled: Boolean(eligibleBookingId),
    staleTime: 1000 * 60 * 5,
  })

  if (!eligibleBookingId || alreadyReviewed) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (rating === 0) { toast.error('Selecione uma nota de 1 a 5.'); return }
    if (!eligibleBookingId) return
    setSubmitting(true)
    try {
      const { error } = await supabase.from('reviews').insert({
        booking_id: eligibleBookingId,
        student_id: userId,
        instructor_id: instructorId,
        rating,
        comment: comment.trim() || null,
        anonymous: !showName,
      })
      if (error) throw error
      toast.success('Avaliação enviada! Obrigado.')
      await queryClient.invalidateQueries({ queryKey: instructorKeys.detail(instructorSlug) })
      await queryClient.invalidateQueries({ queryKey: ['already-reviewed', userId, instructorId] })
    } catch {
      toast.error('Erro ao enviar avaliação.')
    } finally {
      setSubmitting(false)
    }
  }

  const RATING_LABELS = ['', 'Muito ruim', 'Ruim', 'Regular', 'Bom', 'Excelente!']

  return (
    <div className="bg-white rounded-2xl border border-brand-100 shadow-sm p-6 space-y-4">
      <h3 className="font-semibold text-gray-900">Deixe sua avaliação</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <p className="text-sm text-gray-500">Nota geral</p>
          <StarPicker value={rating} onChange={setRating} />
          {rating > 0 && (
            <p className="text-xs text-muted-foreground">{RATING_LABELS[rating]}</p>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-sm text-gray-500">Sugestões rápidas</p>
          <div className="flex flex-wrap gap-2">
            {REVIEW_TAGS.map((tag) => {
              const active = comment.includes(tag)
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() =>
                    active
                      ? setComment((c) => c.replace(tag, '').replace(/  +/, ' ').trim())
                      : setComment((c) => (c ? `${c.trimEnd()} ${tag}` : tag))
                  }
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                    active
                      ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-yellow-300 hover:text-yellow-700'
                  }`}
                >
                  {active ? '✓ ' : ''}{tag}
                </button>
              )
            })}
          </div>
        </div>

        <div className="space-y-1.5">
          <p className="text-sm text-gray-500">Comentário (opcional)</p>
          <Textarea
            placeholder="Conte como foi sua experiência com este instrutor..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground text-right">{comment.length}/500</p>
        </div>

        <label className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 cursor-pointer">
          <span className="text-sm text-gray-700">
            Exibir meu nome na avaliação
            <span className="block text-xs text-muted-foreground mt-0.5">
              Desligue para aparecer como "Aluno verificado"
            </span>
          </span>
          <input
            type="checkbox"
            className="h-5 w-9 appearance-none rounded-full bg-gray-200 checked:bg-brand-600 transition-colors relative before:absolute before:top-0.5 before:left-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:transition-transform checked:before:translate-x-4"
            checked={showName}
            onChange={(e) => setShowName(e.target.checked)}
          />
        </label>

        <Button type="submit" disabled={submitting || rating === 0} className="gap-1.5">
          <Send className="h-4 w-4" />
          {submitting ? 'Enviando...' : 'Enviar avaliação'}
        </Button>
      </form>
    </div>
  )
}

// ─── Waitlist button ──────────────────────────────────────────────────────────

function WaitlistButton({ instructorId }: { instructorId: string }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: joined, isLoading } = useQuery({
    queryKey: ['waitlist', instructorId, user?.id ?? ''],
    queryFn: async () => {
      if (!user?.id) return false
      const { data } = await supabase
        .from('instructor_waitlist')
        .select('id')
        .eq('instructor_id', instructorId)
        .eq('student_id', user.id)
        .maybeSingle()
      return Boolean(data)
    },
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
  })

  async function toggle() {
    if (!user?.id) {
      toast.error('Faça login para entrar na lista de espera.')
      return
    }
    if (joined) {
      await supabase
        .from('instructor_waitlist')
        .delete()
        .eq('instructor_id', instructorId)
        .eq('student_id', user.id)
    } else {
      await supabase
        .from('instructor_waitlist')
        .insert({ instructor_id: instructorId, student_id: user.id })
    }
    queryClient.invalidateQueries({ queryKey: ['waitlist', instructorId, user.id] })
  }

  return (
    <button
      onClick={toggle}
      disabled={isLoading}
      className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors disabled:opacity-60 ${
        joined
          ? 'text-teal-600 hover:text-teal-700'
          : 'text-gray-400 hover:text-teal-600'
      }`}
    >
      <Bell className={`h-4 w-4 ${joined ? 'fill-current' : ''}`} />
      {joined ? 'Na lista de espera' : 'Me avise quando disponível'}
    </button>
  )
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ProfileSkeleton() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
      <div className="bg-white rounded-2xl border border-gray-100 p-8 mb-6">
        <div className="flex gap-6">
          <div className="h-28 w-28 rounded-full bg-gray-200 flex-shrink-0" />
          <div className="flex-1 space-y-3 pt-2">
            <div className="h-7 bg-gray-200 rounded w-48" />
            <div className="h-4 bg-gray-200 rounded w-32" />
            <div className="flex gap-2">
              <div className="h-6 w-20 bg-gray-200 rounded-full" />
              <div className="h-6 w-24 bg-gray-200 rounded-full" />
            </div>
          </div>
        </div>
      </div>
      <div className="h-10 bg-gray-200 rounded-xl w-full mb-6" />
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-gray-200 rounded-xl" />
        ))}
      </div>
    </div>
  )
}

// ─── Booking CTA ──────────────────────────────────────────────────────────────

interface BookingCTAProps {
  instructor: InstructorWithProfile
  duration: number
  onDurationChange: (d: number) => void
  onBook: () => void
  onJoinWaitlist?: () => void
  onAsk?: () => void
}

function BookingCTA({ instructor, duration, onDurationChange, onBook, onJoinWaitlist, onAsk }: BookingCTAProps) {
  // Preço ao aluno (instrutor + 40 %).
  const hourlyRate = getStudentPrice(instructor.hourly_rate ?? 0)
  const totalPrice = (hourlyRate * duration) / 60
  const hasAvailability = ((instructor as any).availability ?? []).length > 0

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-bold text-gray-900">
          R$ {hourlyRate.toFixed(2).replace('.', ',')}
        </span>
        <span className="text-gray-400">/hora</span>
      </div>

      {!hasAvailability && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
          <Calendar className="h-4 w-4 shrink-0 text-amber-500" />
          Sem vagas abertas no momento — entre na lista de espera
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-600">Duração da aula</p>
        <div className="flex gap-2">
          {DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.minutes}
              type="button"
              onClick={() => onDurationChange(opt.minutes)}
              className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                duration === opt.minutes
                  ? 'border-brand-600 bg-brand-50 text-brand-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {duration !== 60 && (
        <p className="text-sm text-gray-500">
          Total:{' '}
          <span className="font-semibold text-gray-800">
            R$ {totalPrice.toFixed(2).replace('.', ',')}
          </span>
        </p>
      )}

      {hasAvailability ? (
        <Button
          type="button"
          onClick={onBook}
          className="w-full h-12 text-base font-semibold bg-brand-600 hover:bg-brand-700"
        >
          <Calendar className="h-5 w-5 mr-2" />
          Agendar aula
        </Button>
      ) : (
        <Button
          type="button"
          onClick={onJoinWaitlist}
          variant="outline"
          className="w-full h-12 text-base font-semibold border-amber-300 text-amber-800 hover:bg-amber-50"
          disabled={!onJoinWaitlist}
        >
          <Calendar className="h-5 w-5 mr-2" />
          Entrar na lista de espera
        </Button>
      )}

      {onAsk && (
        <Button type="button" variant="outline" onClick={onAsk} className="w-full gap-2">
          <MessageCircle className="h-4 w-4" />
          Tirar dúvida antes de agendar
        </Button>
      )}

      {hasAvailability && (
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
          <CreditCard className="h-4 w-4" />
          Pagamento seguro via Mercado Pago
        </div>
      )}
    </div>
  )
}

// ─── Pacotes do instrutor ─────────────────────────────────────────────────────
// O instrutor define o preço do pacote; o aluno vê e paga esse preço + a taxa
// da plataforma (getStudentPrice). `purchase-package` recalcula no servidor.

function InstructorPackagesSection({ instructorId, canBuy, onNeedLogin }: {
  instructorId: string
  canBuy: boolean
  onNeedLogin: () => void
}) {
  const { data: packages } = useInstructorPackages(instructorId)
  const { data: myPackages } = useUsablePackagesForInstructor(canBuy ? instructorId : undefined)
  const purchase = usePurchasePackage()
  const active = (packages ?? []).filter((p) => p.is_active)
  if (active.length === 0) return null

  const credits = myPackages.reduce((n, p) => n + p.lessons_remaining, 0)

  return (
    <div id="pacotes" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 scroll-mt-6">
      <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <Package className="h-5 w-5 text-brand-600" />
        Pacotes de aulas
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Pago com o saldo da sua carteira (se faltar, você paga só a diferença via Pix). Agende as
        aulas (1 hora cada) quando quiser, em até 12 meses.
      </p>
      {credits > 0 && (
        <p className="mb-4 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
          Você tem {credits} aula{credits !== 1 ? 's' : ''} de pacote com este instrutor. Use em “Agendar aula”.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {active.map((pkg) => {
          const total = getStudentPrice(pkg.price)
          const buying = purchase.isPending && purchase.variables?.package_id === pkg.id
          return (
            <div key={pkg.id} className="rounded-xl border border-gray-200 p-4 flex flex-col">
              <p className="font-semibold text-gray-900">{pkg.name}</p>
              <p className="text-sm text-gray-500">{pkg.lesson_count} aulas de 1 hora</p>
              {pkg.description && <p className="mt-1 text-xs text-gray-500">{pkg.description}</p>}
              <p className="mt-3 text-xl font-bold text-gray-900">{formatCurrency(total)}</p>
              <p className="text-xs text-gray-500">{formatCurrency(total / pkg.lesson_count)}/aula</p>
              <Button
                type="button"
                className="mt-3 bg-brand-600 hover:bg-brand-700"
                disabled={purchase.isPending}
                onClick={() => (canBuy ? purchase.mutate({ package_id: pkg.id }) : onNeedLogin())}
              >
                {buying ? 'Processando...' : 'Comprar pacote'}
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Profile QR Code card (desktop sidebar) ───────────────────────────────────

function ProfileQRCard({ url, instructorName }: { url: string; instructorName: string }) {
  function handleDownload() {
    const svg = document.getElementById('profile-qr-svg') as SVGSVGElement | null
    if (!svg) return
    const xml = new XMLSerializer().serializeToString(svg)
    const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `qr-${instructorName.toLowerCase().replace(/\s+/g, '-')}.svg`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">QR Code do perfil</h3>
      <div className="flex flex-col items-center gap-3">
        <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
          <QRCodeSVG
            id="profile-qr-svg"
            value={url}
            size={152}
            level="M"
            includeMargin={false}
          />
        </div>
        <p className="text-xs text-muted-foreground text-center leading-snug">
          Aponte a câmera para acessar este perfil
        </p>
        <Button variant="outline" size="sm" className="w-full gap-2" onClick={handleDownload}>
          <Download className="h-4 w-4" />
          Baixar QR Code
        </Button>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function InstructorProfilePage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const [duration, setDuration] = useState(60)

  const { data: instructorData, isLoading, isError } = useInstructor(slug ?? '')
  const { data: enhanced } = useInstructorEnhancedFields(instructorData?.id)
  const { data: instructorTagIds = [] } = useInstructorTags(instructorData?.id ?? '')
  const { data: tagDefinitions = [] } = useAllTagDefinitions()
  const { data: favIds } = useFavoriteIds()
  const toggleFav = useToggleFavorite()
  const [copied, setCopied] = useState(false)
  const [visibleReviews, setVisibleReviews] = useState(REVIEWS_PER_PAGE)
  const [waitlistOpen, setWaitlistOpen] = useState(false)
  const [askOpen, setAskOpen] = useState(false)
  const [waitlistPref, setWaitlistPref] = useState('')
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false)

  async function handleShare() {
    const url = window.location.href
    const name = (instructorData as any)?.profile?.full_name ?? 'instrutor'
    if (navigator.share) {
      try {
        await navigator.share({ title: `${name} — MeuDriver`, url })
      } catch {
        // Usuário cancelou a folha de compartilhamento — não é erro.
      }
    } else {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      toast.success('Link copiado para a área de transferência!')
      setTimeout(() => setCopied(false), 2000)
    }
  }

    // Map raw Supabase data to typed shapes the page components expect
  const instructor = instructorData ?? null
  const isOwnProfile = !!user && user.id === (instructorData as any)?.profile_id

  const reviews: Review[] = ((instructorData as any)?.reviews ?? []).map((r: any) => ({
    id: r.id,
    student_name: r.anonymous ? 'Aluno verificado' : (r.student?.full_name ?? 'Aluno'),
    student_avatar: r.anonymous ? undefined : (r.student?.avatar_url ?? undefined),
    student_is_premium: r.anonymous ? false : (r.student?.is_premium ?? false),
    rating: r.rating,
    comment: r.comment ?? '',
    instructor_reply: r.instructor_reply ?? null,
    instructor_reply_at: r.instructor_reply_at ?? null,
    created_at: r.created_at,
  }))

  const availability: DayAvailability[] = (() => {
    const raw: { day_of_week: number; start_time: string; end_time: string }[] =
      (instructorData as any)?.availability ?? []
    return DAY_OF_WEEK_LABELS.map((dayName, idx) => {
      const slots = raw
        .filter((a) => a.day_of_week === idx)
        .map((a) => ({ start: a.start_time.slice(0, 5), end: a.end_time.slice(0, 5) }))
      return { day: dayName, slots }
    }).filter((d) => d.slots.length > 0)
  })()

  const notFound = isError || (!isLoading && !instructor)

  const p2 = (instructorData as any)?.profile
  const seoTitle = p2?.full_name
    ? `${p2.full_name} — Instrutor de Direção${p2.city ? ` em ${p2.city}` : ''}`
    : undefined
  const seoDesc = (instructorData as any)?.bio
    ? `${(instructorData as any).bio.slice(0, 150)}...`
    : p2?.full_name
    ? `Agende aulas de direção com ${p2.full_name}. Confira avaliações, disponibilidade e valores.`
    : undefined

  useSEO({
    title: seoTitle,
    description: seoDesc,
    image: p2?.avatar_url ?? undefined,
    url: window.location.href,
    type: 'profile',
  })

  const handleBook = () => {
    if (!user) {
      navigate('/login')
      return
    }
    if (profile?.role !== 'student') {
      navigate('/login')
      return
    }
    navigate(`/aluno/agendar/${instructor?.id}`)
  }

  // Conversa antes de agendar (inquiry_messages). Só aluno logado pergunta.
  const handleAsk = () => {
    if (!user || profile?.role !== 'student') {
      navigate('/login')
      return
    }
    setAskOpen(true)
  }

  const handleJoinWaitlist = () => {
    if (!user) {
      navigate('/login')
      return
    }
    if (profile?.role !== 'student') {
      navigate('/login')
      return
    }
    setWaitlistOpen(true)
  }

  async function submitWaitlist() {
    if (!user || !instructor) return
    setWaitlistSubmitting(true)
    try {
      const { error } = await supabase.from('instructor_waitlist').insert({
        instructor_id: instructor.id,
        student_id: user.id,
        // preferred_time não está no schema atual — guardamos como nota no campo
        // existente quando ele for adicionado. Por ora ignoramos preferência.
      })
      if (error) {
        if (error.code === '23505') {
          toast.info('Você já está na lista de espera deste instrutor.')
        } else {
          throw error
        }
      } else {
        toast.success('Você entrou na lista de espera. Avisaremos quando houver vaga!')
      }
      setWaitlistOpen(false)
      setWaitlistPref('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao entrar na lista.')
    } finally {
      setWaitlistSubmitting(false)
    }
  }

  if (isLoading) return <ProfileSkeleton />

  if (notFound || !instructor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="rounded-full bg-gray-100 p-6 inline-block">
            <AlertCircle className="h-12 w-12 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Instrutor não encontrado
          </h1>
          <p className="text-gray-500">
            Este perfil não existe ou foi removido.
          </p>
          <Button asChild variant="outline">
            <Link to="/instrutores">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Ver todos os instrutores
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  const isFav = favIds?.includes(instructor.id) ?? false

  const ip = instructor
  const p = (instructor as any).profile
  const avgRating =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0

  const initials = (p?.full_name ?? 'IN')
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <Link
          to="/instrutores"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Voltar para instrutores
        </Link>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: profile content */}
          <div className="flex-1 min-w-0 space-y-6">
            {/* Hero card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row gap-6">
                <Avatar className="h-28 w-28 flex-shrink-0 ring-4 ring-white shadow-md">
                  <AvatarImage
                    src={p?.avatar_url ?? undefined}
                    alt={p?.full_name ?? 'Instrutor'}
                  />
                  <AvatarFallback className="text-2xl font-bold bg-brand-100 text-brand-700">
                    {initials}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 space-y-3">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                      {p?.full_name ?? 'Instrutor'}
                    </h1>
                    {(p?.city || instructor.latitude) && (
                      <p className="flex items-center gap-1.5 text-gray-500 mt-1">
                        <MapPin className="h-4 w-4" />
                        {p?.city}{p?.city && p?.state ? ', ' : ''}{p?.state}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {reviews.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="gap-1.5 bg-yellow-50 text-yellow-700 border-yellow-200"
                      >
                        <Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />
                        {avgRating.toFixed(1)} · {reviews.length} avaliações
                      </Badge>
                    )}
                    {ip?.total_lessons != null && (
                      <Badge variant="secondary" className="gap-1.5">
                        <BookOpen className="h-3.5 w-3.5" />
                        {ip.total_lessons} aulas dadas
                      </Badge>
                    )}
                    <Badge
                      variant="secondary"
                      className={
                        instructor.status === 'approved'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-gray-50 text-gray-500'
                      }
                    >
                      <div
                        className={`h-2 w-2 rounded-full mr-1.5 ${
                          instructor.status === 'approved' ? 'bg-green-500' : 'bg-gray-400'
                        }`}
                      />
                      {instructor.status === 'approved' ? 'Ativo' : 'Inativo'}
                    </Badge>
                    {/* O1 — Badge de férias */}
                    {(instructor as any).vacation_mode && (
                      <Badge
                        variant="secondary"
                        className="gap-1.5 bg-amber-50 text-amber-700 border-amber-200"
                      >
                        <Palmtree className="h-3.5 w-3.5" />
                        Em férias
                        {(instructor as any).vacation_end
                          ? ` — retorna em ${format(
                              new Date((instructor as any).vacation_end + 'T12:00:00'),
                              "d 'de' MMM",
                              { locale: ptBR },
                            )}`
                          : ''
                        }
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    {profile?.role === 'student' && (
                      <button
                        onClick={() => toggleFav.mutate({ instructorId: instructor.id, isFav })}
                        className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
                          isFav
                            ? 'text-rose-500 hover:text-rose-700'
                            : 'text-gray-400 hover:text-rose-500'
                        }`}
                      >
                        <Heart className={`h-4 w-4 ${isFav ? 'fill-current' : ''}`} />
                        {isFav ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                      </button>
                    )}
                    {profile?.role === 'student' && (
                      <WaitlistButton instructorId={instructor.id} />
                    )}
                    <button
                      onClick={handleShare}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-brand-600 transition-colors"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Share2 className="h-4 w-4" />
                      )}
                      {copied ? 'Link copiado!' : 'Compartilhar'}
                    </button>
                  </div>

                  {/* Social / contact links */}
                  {(ip?.whatsapp || ip?.instagram_url || ip?.linkedin_url) && (
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {ip.whatsapp && (
                        <a
                          href={`https://wa.me/55${ip.whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
                        >
                          <Phone className="h-3.5 w-3.5" />
                          WhatsApp
                        </a>
                      )}
                      {ip.instagram_url && (
                        <a
                          href={ip.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pink-50 border border-pink-200 text-xs font-medium text-pink-700 hover:bg-pink-100 transition-colors"
                        >
                          <Instagram className="h-3.5 w-3.5" />
                          Instagram
                        </a>
                      )}
                      {ip.linkedin_url && (
                        <a
                          href={ip.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                        >
                          <Linkedin className="h-3.5 w-3.5" />
                          LinkedIn
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Anúncio fixado */}
            {instructor && (
              <InstructorAnnouncementCard instructorId={instructor.id} />
            )}

            {/* Tabs */}
            <Tabs defaultValue="profile">
              <TabsList className="w-full grid grid-cols-6">
                <TabsTrigger value="profile">Perfil</TabsTrigger>
                <TabsTrigger value="reviews">
                  Avaliações
                  {reviews.length > 0 && (
                    <span className="ml-1.5 text-xs bg-gray-200 text-gray-600 rounded-full px-1.5">
                      {reviews.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="gallery">Galeria</TabsTrigger>
                <TabsTrigger value="availability">Disponibilidade</TabsTrigger>
                <TabsTrigger value="area">Área</TabsTrigger>
                <TabsTrigger value="feed">Novidades</TabsTrigger>
              </TabsList>

              {/* ── Tab: Perfil ── */}
              <TabsContent value="profile" className="mt-4 space-y-5">
                {/* Bio */}
                {ip?.bio && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <h2 className="font-semibold text-gray-900 mb-3">Sobre</h2>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                      {ip.bio}
                    </p>
                  </div>
                )}

                {/* Details grid */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h2 className="font-semibold text-gray-900 mb-4">Detalhes</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {ip?.years_experience != null && (
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-brand-50 p-2">
                          <Award className="h-5 w-5 text-brand-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Experiência</p>
                          <p className="font-medium text-gray-800">
                            {ip.years_experience}{' '}
                            {ip.years_experience === 1 ? 'ano' : 'anos'}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-green-50 p-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">CNH</p>
                        <p className="font-medium text-gray-800">
                          {ip?.cnh_verified ? (
                            <span className="text-green-600">Verificada</span>
                          ) : (
                            'Não verificada'
                          )}
                        </p>
                      </div>
                    </div>

                    {ip?.vehicle_model && (
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-gray-100 p-2">
                          <Car className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Veículo</p>
                          <p className="font-medium text-gray-800">
                            {ip.vehicle_model}
                            {ip.vehicle_year ? ` · ${ip.vehicle_year}` : ''}
                          </p>
                        </div>
                      </div>
                    )}

                    {ip?.service_radius_km != null && (
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-orange-50 p-2">
                          <MapPin className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Raio de atendimento</p>
                          <p className="font-medium text-gray-800">
                            {ip.service_radius_km} km
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vehicle gallery */}
                {ip?.vehicle_photos?.length > 0 && (
                  <VehicleGallery photos={ip.vehicle_photos} />
                )}

                {/* R2: Veículos disponíveis (instructor_vehicles) */}
                <InstructorVehiclesSection instructorId={instructor.id} />

                <InstructorPackagesSection
                  instructorId={instructor.id}
                  canBuy={!!user && profile?.role === 'student'}
                  onNeedLogin={() => navigate('/login')}
                />

                {/* Specialties */}
                {(instructor as any).specialties?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Award className="h-5 w-5 text-brand-600" />
                      Especialidades
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {((instructor as any).specialties as { specialty: string }[]).map(({ specialty }) => (
                        <Badge key={specialty} className="bg-brand-50 text-brand-700 border-brand-200">
                          {SPECIALTY_LABELS[specialty as keyof typeof SPECIALTY_LABELS] ?? specialty}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Batch U3 — Tags de especialidade */}
                {instructorTagIds.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Award className="h-5 w-5 text-brand-600" />
                      Diferenciais
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {instructorTagIds.map((tagId) => {
                        const def = tagDefinitions.find((d) => d.id === tagId)
                        if (!def) return null
                        return (
                          <span
                            key={tagId}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-brand-50 text-brand-700 border border-brand-200"
                          >
                            {def.label}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Highlights (bio complementar) */}
                {enhanced?.highlights && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-gray-500" />
                      Destaques
                    </h2>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line text-sm">
                      {enhanced.highlights}
                    </p>
                  </div>
                )}

                {/* Experiência aprimorada */}
                {(enhanced?.teaching_since || (enhanced?.certifications?.length ?? 0) > 0) && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-3">
                    <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                      <GraduationCap className="h-5 w-5 text-brand-600" />
                      Experiência e formação
                    </h2>
                    {enhanced?.teaching_since && (
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-brand-50 p-2 shrink-0">
                          <CalendarDays className="h-4 w-4 text-brand-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Leciona desde</p>
                          <p className="text-sm font-medium text-gray-800">
                            {new Date(enhanced.teaching_since).toLocaleDateString('pt-BR', {
                              month: 'long',
                              year: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>
                    )}
                    {enhanced?.certifications && enhanced.certifications.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Certificações</p>
                        <ul className="space-y-1">
                          {enhanced.certifications.map((cert, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                              {cert}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Idiomas */}
                {enhanced?.languages && enhanced.languages.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Languages className="h-5 w-5 text-gray-500" />
                      Idiomas
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {enhanced.languages.map((lang) => (
                        <Badge key={lang} variant="secondary" className="capitalize">
                          {LANGUAGE_LABELS[lang] ?? lang}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vídeo de apresentação */}
                {enhanced?.video_intro_url && (() => {
                  // S1-MEDIO-001: valida protocolo antes de renderizar link externo.
                  // Rejeita javascript:, data: e qualquer protocolo não-http(s).
                  const videoUrl = enhanced.video_intro_url
                  const isSafeUrl = /^https?:\/\//i.test(videoUrl)
                  return (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                      <h2 className="font-semibold text-gray-900 mb-3">Vídeo de apresentação</h2>
                      {isSafeUrl ? (
                        <a
                          href={videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm font-medium text-red-700 hover:bg-red-100 transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Ver vídeo de apresentação
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-400 cursor-not-allowed">
                          <ExternalLink className="h-4 w-4" />
                          Link de vídeo indisponível
                        </span>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        Abre em uma nova aba. Verifique a URL antes de acessar.
                      </p>
                    </div>
                  )
                })()}

                {/* Pricing (mobile inline view) */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 lg:hidden">
                  <h2 className="font-semibold text-gray-900 mb-4">Agendar aula</h2>
                  <BookingCTA
                    instructor={instructor}
                    duration={duration}
                    onDurationChange={setDuration}
                    onBook={handleBook}
                    onJoinWaitlist={handleJoinWaitlist}
                    onAsk={isOwnProfile ? undefined : handleAsk}
                  />
                </div>
              </TabsContent>

              {/* ── Tab: Avaliações ── */}
              <TabsContent value="reviews" className="mt-4 space-y-5">
                {user && profile?.role === 'student' && instructor && (
                  <WriteReviewForm
                    instructorId={instructor.id}
                    instructorSlug={slug ?? ''}
                    userId={user.id}
                  />
                )}

                {reviews.length > 0 ? (
                  <>
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                      <RatingBreakdown reviews={reviews} />
                    </div>
                    <div className="space-y-3">
                      {reviews.slice(0, visibleReviews).map((review) => (
                        <ReviewCard key={review.id} review={review} isOwnProfile={isOwnProfile} />
                      ))}
                    </div>
                    {visibleReviews < reviews.length && (
                      <button
                        onClick={() => setVisibleReviews((v) => v + REVIEWS_PER_PAGE)}
                        className="w-full py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all"
                      >
                        Ver mais {Math.min(REVIEWS_PER_PAGE, reviews.length - visibleReviews)} avaliações
                      </button>
                    )}
                    {visibleReviews > REVIEWS_PER_PAGE && visibleReviews >= reviews.length && (
                      <button
                        onClick={() => setVisibleReviews(REVIEWS_PER_PAGE)}
                        className="w-full py-2 text-xs text-muted-foreground hover:text-gray-700 transition-colors"
                      >
                        Mostrar menos
                      </button>
                    )}
                  </>
                ) : (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
                    <MessageCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="font-medium text-gray-600">Nenhuma avaliação ainda</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Seja o primeiro a avaliar este instrutor!
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* ── Tab: Disponibilidade ── */}
              <TabsContent value="availability" className="mt-4">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-gray-500" />
                    Horários disponíveis
                  </h2>
                  <AvailabilityTable availability={availability} />
                </div>
              </TabsContent>

              {/* ── Tab: Área de atendimento ── */}
              <TabsContent value="area" className="mt-4">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-orange-600" />
                    Área de atendimento
                  </h2>
                  {ip?.latitude != null && ip?.longitude != null && ip?.service_radius_km != null ? (
                    <>
                      <p className="text-sm text-gray-600 mb-4">
                        Este instrutor atende num raio de{' '}
                        <strong>{ip.service_radius_km} km</strong> a partir do ponto central.
                      </p>
                      <InstructorServiceAreaMap
                        centerLat={ip.latitude}
                        centerLng={ip.longitude}
                        radiusKm={ip.service_radius_km}
                        instructorName={p?.full_name ?? undefined}
                      />
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      O instrutor ainda não definiu a área de atendimento no mapa.
                    </p>
                  )}
                </div>
              </TabsContent>

              {/* ── Tab: Galeria ── */}
              <TabsContent value="gallery" className="mt-4">
                {instructor
                  ? <InstructorGalleryTab instructorId={instructor.id} />
                  : null
                }
              </TabsContent>

              {/* ── Tab: Novidades (feed) ── */}
              <TabsContent value="feed" className="mt-4">
                {instructor && <InstructorFeedCard instructorId={instructor.id} />}
              </TabsContent>
            </Tabs>
          </div>

          {/* Right: booking sidebar (desktop) */}
          <aside className="hidden lg:block w-80 flex-shrink-0 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sticky top-6">
              <BookingCTA
                instructor={instructor}
                duration={duration}
                onDurationChange={setDuration}
                onBook={handleBook}
                onJoinWaitlist={handleJoinWaitlist}
                onAsk={isOwnProfile ? undefined : handleAsk}
              />
            </div>
            {/* QR para divulgar o perfil: só serve ao próprio instrutor */}
            {isOwnProfile && (
              <ProfileQRCard
                url={window.location.href}
                instructorName={p?.full_name ?? 'instrutor'}
              />
            )}
          </aside>
        </div>
      </div>

      {/* Mobile sticky CTA bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg p-4 z-50">
        <div className="flex items-center gap-4">
          <div>
            <p className="text-xs text-gray-400">A partir de</p>
            <p className="text-xl font-bold text-gray-900">
              R$ {getStudentPrice(instructor.hourly_rate ?? 0)
                .toFixed(2)
                .replace('.', ',')}
              <span className="text-sm font-normal text-gray-400">/h</span>
            </p>
          </div>
          {((instructor as any).availability ?? []).length > 0 ? (
            <Button
              type="button"
              onClick={handleBook}
              className="flex-1 h-12 text-base font-semibold bg-brand-600 hover:bg-brand-700"
            >
              <Calendar className="h-5 w-5 mr-2" />
              Agendar aula
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleJoinWaitlist}
              variant="outline"
              className="flex-1 h-12 text-base font-semibold border-amber-300 text-amber-800 hover:bg-amber-50"
            >
              <Calendar className="h-5 w-5 mr-2" />
              Lista de espera
            </Button>
          )}
        </div>
      </div>

      {/* Dialog: tirar dúvida */}
      {user && instructor && profile?.role === 'student' && (
        <Dialog open={askOpen} onOpenChange={setAskOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Conversar com {(instructor as any).profile?.full_name?.split(' ')[0] ?? 'o instrutor'}</DialogTitle>
            </DialogHeader>
            <InquiryChat
              studentId={user.id}
              instructorId={instructor.id}
              otherName={(instructor as any).profile?.full_name?.split(' ')[0] ?? 'o instrutor'}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog waitlist */}
      <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lista de espera</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Você será notificado(a) quando este instrutor abrir novos horários.
            </p>
            <div className="space-y-1.5">
              <label className="font-medium">Preferência de horário (opcional)</label>
              <textarea
                rows={3}
                maxLength={200}
                value={waitlistPref}
                onChange={(e) => setWaitlistPref(e.target.value)}
                placeholder="Ex: manhãs de sábado, ou qualquer horário entre 18h e 21h"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setWaitlistOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={submitWaitlist}
              disabled={waitlistSubmitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {waitlistSubmitting ? 'Enviando...' : 'Entrar na lista'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom padding for mobile CTA */}
      <div className="lg:hidden h-24" />
    </div>
  )
}
